import React, { useState, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Upload, CheckCircle, XCircle, AlertTriangle, FileText, Play, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommissionsImport = () => {
  const { getAuthHeaders } = useAuth();
  const { tenant } = useTenant();

  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [previewResult, setPreviewResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [showUpdated, setShowUpdated] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setPreviewResult(null);
    setApplyResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
    e.target.value = '';
  };

  const runImport = useCallback(async (dryRun) => {
    if (!csvText) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/commissions/csv-import?dry_run=${dryRun}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv_text: csvText, tenant }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      if (dryRun) {
        setPreviewResult(data);
      } else {
        setApplyResult(data);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }, [csvText, getAuthHeaders, tenant]);

  const result = applyResult || previewResult;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commissions CSV Import</h1>
          <p className="text-sm text-gray-500 mt-1">
            Fuzzy-matches deal names from the CSV against existing deals and backfills blank commission fields.
            Only blank fields are updated — existing data is never overwritten.
          </p>
        </div>

        {/* Upload zone */}
        <Card>
          <CardContent className="p-6">
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
              data-testid="csv-drop-zone"
            >
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              {fileName ? (
                <div>
                  <p className="font-medium text-gray-800">{fileName}</p>
                  <p className="text-xs text-gray-500 mt-1">Click to change file</p>
                </div>
              ) : (
                <div>
                  <p className="font-medium text-gray-700">Click to upload CSV</p>
                  <p className="text-xs text-gray-500 mt-1">Main Commissions CSV with deal names and broker data</p>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} data-testid="csv-file-input" />

            {csvText && (
              <div className="mt-4 flex gap-3 justify-end">
                {!applyResult && (
                  <button
                    onClick={() => runImport(true)}
                    disabled={loading}
                    className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg text-sm hover:bg-blue-50 disabled:opacity-50 flex items-center gap-2"
                    data-testid="preview-btn"
                  >
                    <FileText className="h-4 w-4" />
                    {loading && !previewResult ? 'Previewing...' : 'Preview Import'}
                  </button>
                )}
                {previewResult && !applyResult && (
                  <button
                    onClick={() => runImport(false)}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                    data-testid="apply-btn"
                  >
                    <Play className="h-4 w-4" />
                    {loading ? 'Applying...' : `Apply ${previewResult.deals_matched_and_updated} Updates`}
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <div className="space-y-4" data-testid="import-results">
            {/* Status banner */}
            <div className={`rounded-lg px-4 py-3 flex items-center gap-3 ${applyResult ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
              {applyResult ? <CheckCircle className="h-5 w-5 text-green-600 shrink-0" /> : <FileText className="h-5 w-5 text-blue-600 shrink-0" />}
              <span className="font-medium text-sm">
                {applyResult ? 'Import applied successfully.' : 'Preview only — no changes made yet.'}
              </span>
              {result.dry_run === false && <Badge className="bg-green-600 text-white ml-auto">APPLIED</Badge>}
              {result.dry_run === true && <Badge variant="outline" className="ml-auto text-blue-700 border-blue-400">DRY RUN</Badge>}
            </div>

            {/* Summary stat cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Total CSV rows', value: result.total_rows_in_csv, color: 'text-gray-700' },
                { label: 'Blank / skipped', value: result.blank_skipped, color: 'text-gray-500' },
                { label: 'Rows processed', value: result.rows_processed, color: 'text-blue-700' },
                { label: 'Deals updated', value: result.deals_matched_and_updated, color: 'text-green-700' },
              ].map(s => (
                <Card key={s.label}>
                  <CardContent className="p-4 text-center">
                    <div className={`text-3xl font-bold ${s.color}`}>{s.value}</div>
                    <div className="text-xs text-gray-500 mt-1">{s.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Unmatched */}
            {result.deals_not_matched > 0 && (
              <Card className="border-amber-200">
                <CardHeader className="pb-2 cursor-pointer" onClick={() => setShowUnmatched(v => !v)}>
                  <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-4 w-4" />
                    {result.deals_not_matched} rows could not be matched
                    {showUnmatched ? <ChevronDown className="h-4 w-4 ml-auto" /> : <ChevronRight className="h-4 w-4 ml-auto" />}
                  </CardTitle>
                </CardHeader>
                {showUnmatched && (
                  <CardContent className="pt-0">
                    <p className="text-xs text-gray-500 mb-2">These deal names had no close match in your contacts database. Review manually.</p>
                    <div className="space-y-1">
                      {[...new Set(result.unmatched_names)].map((name, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 px-3 py-1.5 rounded">
                          <XCircle className="h-3.5 w-3.5 shrink-0" />
                          {name}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* Updated deals preview */}
            {result.updated_deals_preview?.length > 0 && (
              <Card>
                <CardHeader className="pb-2 cursor-pointer" onClick={() => setShowUpdated(v => !v)}>
                  <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                    <CheckCircle className="h-4 w-4" />
                    {result.deals_matched_and_updated} deals {applyResult ? 'updated' : 'will be updated'}
                    {showUpdated ? <ChevronDown className="h-4 w-4 ml-auto" /> : <ChevronRight className="h-4 w-4 ml-auto" />}
                  </CardTitle>
                </CardHeader>
                {showUpdated && (
                  <CardContent className="pt-0 p-0">
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                      <table className="w-full text-xs" data-testid="import-preview-table">
                        <thead className="sticky top-0 bg-gray-50 border-b">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Deal ID</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">CSV Name</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Matched Contact</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-600">Score</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Fields Updated</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.updated_deals_preview.map((row) => (
                            <tr key={row.deal_id} className={`hover:bg-gray-50 ${row.is_clawback ? 'bg-red-50' : ''}`}>
                              <td className="px-3 py-2 font-mono">{row.deal_id}</td>
                              <td className="px-3 py-2">{row.csv_name}</td>
                              <td className="px-3 py-2 text-gray-500">{row.matched_contact}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`font-medium ${row.match_score >= 90 ? 'text-green-600' : row.match_score >= 75 ? 'text-yellow-600' : 'text-red-600'}`}>
                                  {row.match_score}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${row.is_clawback ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                  {row.type}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {row.fields_updated.map(f => (
                                    <span key={f} className="bg-blue-50 text-blue-700 border border-blue-200 rounded px-1 text-xs">{f}</span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                )}
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CommissionsImport;
